<template>
  <button
    id="userprefs"
    class="
      user-prefs-button
      button button--iconleft button--text
      paragraph-note--sm
    "
    :onClick="openModal"
    style="white-space: nowrap"
    :class="triggerClasses"
  >
    <i
      v-if="!languageOnly"
      class="icon icon-globe icon--xxxs"
      aria-hidden="true"
    />
    <p v-if="languageOnly">{{ langPrefRef.toUpperCase() }}</p>
    <p v-else>
      {{ countryPrefRef.toUpperCase() }} - {{ langPrefRef.toUpperCase() }}
    </p>
  </button>

  <slot name="form"></slot>
  <BaseModal
    :action-required="true"
    modal-class="userprefs-modal"
    triggerIdToFocus="userprefs"
    @update:state="childEmits"
  >
    <template #modal-content>
      <div class="base-model__content">
        <div class="base-model__header base-model__header--border">
          <p class="base-model__header-title heading-h5">
            Set your preferences
          </p>
        </div>
        <div class="base-model__body base-model__body--padded">
          <template v-if="!languageOnly">
            <p class="paragraph-note text--bold text-KQ-Grey1">Country</p>
            <DropdownSelect
              v-if="countryOptions.length > 0"
              :key="countrySelectKey"
              :value-index-on-mount="countryDropdownOnLoadIndex"
              dropdown-label="Select Country"
              :optionsArray="countryOptions"
              @update:value="
                (value) => {
                  valueSelected({
                    name: 'Country',
                    value: value?.value,
                    label: value?.label,
                  });
                }
              "
            />
            <DropdownSelect v-else disabled dropdown-label="Select Country" />
          </template>
          <p
            class="paragraph-note text--bold text-KQ-Grey1"
            :class="{ 'm-t--24': !languageOnly }"
          >
            Language
          </p>
          <DropdownSelect
            v-if="languageOptions.length > 0"
            :key="langSelectKey"
            :value-index-on-mount="langDropdownOnLoadIndex"
            dropdown-label="Select Language"
            :optionsArray="languageOptions"
            @update:value="
              (value) => {
                valueSelected({
                  name: 'Language',
                  value: value?.value,
                  label: value?.label,
                });
              }
            "
          />
          <DropdownSelect v-else disabled dropdown-label="Select Language" />
        </div>
        <div class="base-model__footer">
          <button
            class="button button--text"
            :onClick="
              () => {
                closeModal();
              }
            "
          >
            Cancel
          </button>
          <button
            class="button button--primary"
            :disabled="
              (!props.languageOnly && formValuesRef?.Country === '') ||
              formValuesRef?.Language === ''
            "
            :onClick="
              () => {
                saveHandler();
              }
            "
          >
            Done
          </button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
import { provide, ref, onBeforeMount, watch, onMounted } from 'vue';
import BaseModal from '../BaseModal/BaseModal.vue';
import DropdownSelect from '../DropdownSelect/DropdownSelect.vue';
import { useQuery } from '@tanstack/vue-query';
import optiClient from '../../services/api';

const randomId = Math.random().toString(36).substring(2, 12);
const emit = defineEmits(['close:modal', 'prefs-changed']);
const countryPrefRef = ref(props.countryName);
const langPrefRef = ref(props.languageName);
const currencyPrefRef = ref('KES');
const countryOptions = ref([]);
const languageOptions = ref([]);
const currencyOptions = ref([]);
const countrySelectKey = ref(Math.random().toString(36).substring(2, 12));
const langSelectKey = ref(Math.random().toString(36).substring(2, 12));
const currencySelectKey = ref(Math.random().toString(36).substring(2, 12));
const countryDropdownOnLoadIndex = ref(-1);
const langDropdownOnLoadIndex = ref(-1);
const currencyDropdownOnLoadIndex = ref(-1);
const formValuesRef = ref({
  Country: '',
  Language: '',
});

const isOpen = ref(false);

provide('injectedIsOpen', isOpen);

const props = defineProps({
  triggerClasses: {
    type: String,
    default: '',
  },
  languageOnly: {
    type: Boolean,
    default: false,
  },
  slotButtonId: {
    type: String,
    default: '',
  },
  countryCode: {
    type: String,
    default: '',
  },
  countryName: {
    type: String,
    default: '',
  },
  languageCode: {
    type: String,
    default: '',
  },
  languageName: {
    type: String,
    default: '',
  },
});

const childEmits = (value) => {
  if (value === 'isClosed') {
    closeModal();
  }
};

const openModal = (e) => {
  isOpen.value = true;
};

watch (isOpen, (newValue) => {
  if (!newValue) return;
  if (props.languageOnly) {
    langQuery.refetch();
  } else {
    countryQuery.refetch();
  }
});

const closeModal = (e) => {
  countrySelectKey.value = Math.random().toString(36).substring(2, 12);
  langSelectKey.value = Math.random().toString(36).substring(2, 12);
  isOpen.value = false;
};

const countryQuery = useQuery({
  queryKey: ['preferencesCountry', randomId],
  queryFn: async () => {
    try {
      const res = await optiClient.preferences.getApiV1PreferencesCountries();
      return res;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  enabled: false,
  refetchOnWindowFocus: false,
});

const langQuery = useQuery({
  queryKey: ['preferencesLanguages', randomId],
  queryFn: async () => {
    try {
      const queryData =
        await optiClient.preferences.getApiV1PreferencesLanguages(
          formValuesRef?.value?.Country?.value,
        );
      return queryData;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  enabled: false,
  refetchOnWindowFocus: false,
});

// Set dropdown options and index and cookie name
watch(countryQuery.data, () => {
  if (countryQuery.status?.value === 'success') {
    const dataArray = Object.values(countryQuery.data?.value?.data);
    // Check if props.countryName is in dataArray, and if it is, move it to the start

    const trimmedCountryName = props.countryName.trim();
    // Sort the array by countryName
    dataArray.sort((a, b) => a.countryName.localeCompare(b.countryName));

    const index = dataArray.findIndex(
      (entry) => entry.countryName.trim() === trimmedCountryName,
    );

    if (index !== -1) {
      const currentPrefs = dataArray.splice(index, 1)[0];
      dataArray.unshift(currentPrefs);
    }

    countryOptions.value = dataArray.map((entry) => {
      return { label: entry.countryName, value: entry.countryCode };
    });
    setDropdownValueHandler('Country');
  }
});

// Set dropdown options and index and cookie name
watch(langQuery.data, () => {
  if (langQuery.status?.value === 'success') {
    const dataArray = Object.values(langQuery.data?.value?.data);

    const trimmedLangName = props.languageName.trim();
    dataArray.sort((a, b) => a.languageName.localeCompare(b.languageName));
    const index = dataArray.findIndex(
      (entry) => entry.languageName.trim() === trimmedLangName,
    );

    if (index !== -1) {
      const currentPrefs = dataArray.splice(index, 1)[0];
      dataArray.unshift(currentPrefs);
    }
    languageOptions.value = dataArray.map((entry) => {
      return { label: entry.languageName, value: entry.languageCode };
    });
    setDropdownValueHandler('Language');
  }
});

const valueSelected = (obj) => {
  if (formValuesRef.value[obj.name]?.value === obj.value) {
    return;
  }
  formValuesRef.value[obj.name] = { value: obj.value, label: obj.label };
  if (obj.name === 'Country') {
    resetDropDownHandler();
  }
};

const saveHandler = () => {
  const button = document.getElementById(props.slotButtonId);
  const form = button.closest('form');
  if (form) {
    const countryInput = form.querySelector('input[name="countryCode"]');
    if (countryInput) {
      countryInput.value = formValuesRef?.value?.Country?.value
        ? formValuesRef?.value?.Country?.value
        : '';
    }
    const langInput = form.querySelector('input[name="languageCode"]');
    if (langInput) {
      langInput.value = formValuesRef?.value?.Language?.value
        ? formValuesRef?.value?.Language?.value
        : '';
    }
    form.submit();
  }
};

// Sets the text of the ui components
const setDropdownValueHandler = (name) => {
  switch (name) {
    case 'Country':
      countryDropdownOnLoadIndex.value = countryOptions.value.findIndex(
        (p) => p.value === props.countryCode,
      );
      countrySelectKey.value = Math.random().toString(36).substring(2, 12);
      countryPrefRef.value = props.countryName ? props.countryName : 'Kenya';
      break;
    case 'Language':
      langDropdownOnLoadIndex.value = languageOptions.value.findIndex(
        (p) => p.value === props.languageCode,
      );
      langSelectKey.value = Math.random().toString(36).substring(2, 12);
      langPrefRef.value = props.languageName ? props.languageName : 'English';
      break;
    default:
      break;
  }
};

// Resets Lang  country has been updated
const resetDropDownHandler = () => {
  langQuery.refetch();
  langDropdownOnLoadIndex.value = -1;
  langSelectKey.value = Math.random().toString(36).substring(2, 12);
  currencyDropdownOnLoadIndex.value = -1;
  currencySelectKey.value = Math.random().toString(36).substring(2, 12);
  formValuesRef.value = {
    ...formValuesRef.value,
    Language: '',
  };
};

</script>
